/**
 * 由單一 catalog component 物件（如 `{ Button: { ... } }`）擷取指向子元件的 id。
 */
function extractReferencedChildIds(componentWrapper) {
  if (!componentWrapper || typeof componentWrapper !== 'object') return [];
  const ids = [];

  const walk = (o, depth) => {
    if (!o || typeof o !== 'object' || depth > 14) return;
    if (Array.isArray(o)) {
      for (const item of o) walk(item, depth + 1);
      return;
    }
    for (const [k, v] of Object.entries(o)) {
      if (
        (k === 'child' || k === 'entryPointChild' || k === 'contentChild') &&
        typeof v === 'string'
      ) {
        ids.push(v);
      }
      if (
        k === 'children' &&
        v &&
        typeof v === 'object' &&
        Array.isArray(v.explicitList)
      ) {
        for (const id of v.explicitList) {
          if (typeof id === 'string') ids.push(id);
        }
      }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        walk(v, depth + 1);
      }
    }
  };

  for (const typeKey of Object.keys(componentWrapper)) {
    const node = componentWrapper[typeKey];
    walk(node, 0);
  }

  return ids;
}

/**
 * 若從 beginRendering.root 沿 child／explicitList 可走回自身（含 only_button→only_button），回傳 true。
 * @param {unknown[]} messages A2UI v0.8 server→client 訊息序列
 * @returns {boolean}
 */
export function hasA2uiV08ComponentCycle(messages) {
  if (!Array.isArray(messages)) return false;

  let rootId = null;
  const byId = new Map();

  for (const msg of messages) {
    const br = msg?.beginRendering;
    if (br && typeof br.root === 'string') rootId = br.root;

    const comps = msg?.surfaceUpdate?.components;
    if (!Array.isArray(comps)) continue;
    for (const c of comps) {
      if (c?.id && c.component) byId.set(c.id, c.component);
    }
  }

  if (!rootId || typeof rootId !== 'string') return false;

  const visiting = new Set();
  const visited = new Set();

  function dfs(id) {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    const comp = byId.get(id);
    const children = comp ? extractReferencedChildIds(comp) : [];
    for (const ch of children) {
      if (dfs(ch)) return true;
    }
    visiting.delete(id);
    visited.add(id);
    return false;
  }

  return dfs(rootId);
}
